import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { Sparkles, Loader2, Plus, Trash2, Sliders, ChevronLeft, ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";
import PhotoMealCard from "../components/PhotoMealCard";
import MacroSplitModal from "../components/MacroSplitModal";
import MealPlanSection from "./nutrition/MealPlanSection";

const MEALS = ["breakfast", "lunch", "dinner", "snack"];

/** Local-time YYYY-MM-DD without timezone shenanigans (matches backend
 *  `today_iso()` which uses UTC date — close enough for daily logs). */
const isoDate = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

const shiftDays = (isoStr, delta) => {
  const [y, m, day] = isoStr.split("-").map(Number);
  const d = new Date(y, m - 1, day);
  d.setDate(d.getDate() + delta);
  return isoDate(d);
};

const TODAY = isoDate(new Date());

/** Friendly label for the date header: "Today", "Yesterday", or
 *  "Tuesday · 13 Feb" for anything earlier. */
const friendlyLabel = (iso) => {
  if (iso === TODAY) return "Today";
  if (iso === shiftDays(TODAY, -1)) return "Yesterday";
  const [y, m, day] = iso.split("-").map(Number);
  const d = new Date(y, m - 1, day);
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short" });
};

export default function NutritionPage() {
  const [selectedDate, setSelectedDate] = useState(TODAY);
  const isToday = selectedDate === TODAY;
  const [today, setToday] = useState({ items: [], totals: { calories: 0, protein_g: 0, carbs_g: 0, fat_g: 0 } });
  const [targets, setTargets] = useState(null); // { calories, protein_g, carbs_g, fat_g } from active goal
  const [mode, setMode] = useState("ai"); // 'ai' | 'manual'
  const [copying, setCopying] = useState(false);

  // AI form
  const [desc, setDesc] = useState("");
  const [aiMeal, setAiMeal] = useState("snack");
  const [estimating, setEstimating] = useState(false);

  // Manual form
  const [m, setM] = useState({ name: "", calories: "", protein_g: "", carbs_g: "", fat_g: "", meal: "snack" });
  const [saving, setSaving] = useState(false);
  const [macroModalOpen, setMacroModalOpen] = useState(false);

  const load = useCallback(async (forDate = selectedDate) => {
    const { data } = await api.get(`/food/day`, { params: { date: forDate } });
    setToday(data);
  }, [selectedDate]);

  const loadTargets = async () => {
    try {
      const { data } = await api.get("/goals");
      const weight = (data?.goals || []).find(
        (g) => g.type === "weight" && g.recommended_kcal
      );
      if (weight && weight.macros) {
        setTargets({
          calories: weight.recommended_kcal,
          protein_g: weight.macros.protein_g,
          carbs_g: weight.macros.carbs_g,
          fat_g: weight.macros.fat_g,
        });
      } else {
        setTargets(null);
      }
    } catch (err) {
      // Goals may simply not exist yet — fall back to default targets.
      console.warn("loadTargets failed:", err?.response?.data?.detail || err?.message);
      setTargets(null);
    }
  };

  useEffect(() => {
    load();
    loadTargets();
  }, [load]);

  const copyFromPrevDay = async () => {
    setCopying(true);
    try {
      const from = shiftDays(selectedDate, -1);
      const res = await api.post("/food/copy-day", { from_date: from, to_date: selectedDate });
      toast.success(`Copied ${res.data.copied} meal${res.data.copied === 1 ? "" : "s"} from ${friendlyLabel(from).toLowerCase()}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not copy meals");
    } finally {
      setCopying(false);
    }
  };

  const copyThisDayToToday = async () => {
    setCopying(true);
    try {
      const res = await api.post("/food/copy-day", { from_date: selectedDate, to_date: TODAY });
      toast.success(`Copied ${res.data.copied} meal${res.data.copied === 1 ? "" : "s"} to today`);
      setSelectedDate(TODAY);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not copy to today");
    } finally {
      setCopying(false);
    }
  };

  const aiAdd = async () => {
    if (!desc.trim()) return toast.error("Describe what you ate");
    setEstimating(true);
    try {
      await api.post("/food/ai-estimate", { description: desc, meal: aiMeal });
      setDesc("");
      await load();
      toast.success("Logged with AI estimate");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setEstimating(false);
    }
  };

  const manualAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/food/log", {
        name: m.name,
        calories: Number(m.calories) || 0,
        protein_g: Number(m.protein_g) || 0,
        carbs_g: Number(m.carbs_g) || 0,
        fat_g: Number(m.fat_g) || 0,
        meal: m.meal,
      });
      setM({ name: "", calories: "", protein_g: "", carbs_g: "", fat_g: "", meal: "snack" });
      await load();
      toast.success("Meal logged");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/food/${id}`);
    await load();
  };

  const byMeal = MEALS.map((meal) => ({
    meal,
    items: today.items.filter((i) => i.meal === meal),
  }));

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-up" data-testid="nutrition-page">
      <div className="mb-8 flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="label-eyebrow text-[#6B9BFF] mb-2">Nutrition</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-none">{friendlyLabel(selectedDate)}&apos;s intake</h1>
        </div>
        <div className="flex items-center gap-2">
          {targets?.calories && (
            <button
              type="button"
              onClick={() => setMacroModalOpen(true)}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold border border-white/15 hover:border-[#6B9BFF] hover:text-[#6B9BFF] text-white/70 px-4 py-2.5"
              data-testid="nutrition-edit-macros"
            >
              <Sliders className="w-3.5 h-3.5" /> Adjust macro split
            </button>
          )}
        </div>
      </div>

      {/* Date scroller */}
      <div className="mb-6 flex items-center justify-between border border-white/10 bg-[#121212] px-3 py-2" data-testid="date-scroller">
        <button
          type="button"
          onClick={() => setSelectedDate(shiftDays(selectedDate, -1))}
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-white px-3 py-2"
          data-testid="date-prev"
          aria-label="Previous day"
        >
          <ChevronLeft className="w-4 h-4" />
          {friendlyLabel(shiftDays(selectedDate, -1))}
        </button>
        <div className="text-center min-w-0 px-2">
          <div className="font-bold text-sm text-white truncate" data-testid="date-current-label">
            {friendlyLabel(selectedDate)}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-white/40">
            {selectedDate}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setSelectedDate(shiftDays(selectedDate, 1))}
          disabled={isToday}
          className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed px-3 py-2"
          data-testid="date-next"
          aria-label="Next day"
        >
          {isToday ? "Today" : friendlyLabel(shiftDays(selectedDate, 1))}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="nutrition-totals-grid">
        {[
          { l: "Calories", k: "calories", v: today.totals.calories, t: targets?.calories, u: "kcal", color: "#6B9BFF" },
          { l: "Protein",  k: "protein",  v: Math.round(today.totals.protein_g), t: targets?.protein_g, u: "g", color: "#6B9BFF" },
          { l: "Carbs",    k: "carbs",    v: Math.round(today.totals.carbs_g),   t: targets?.carbs_g,   u: "g", color: "#7C3AED" },
          { l: "Fat",      k: "fat",      v: Math.round(today.totals.fat_g),     t: targets?.fat_g,     u: "g", color: "#FF3B30" },
        ].map((s) => {
          const pct = s.t ? Math.min(100, Math.round((s.v / s.t) * 100)) : null;
          const over = s.t && s.v > s.t;
          return (
            <div
              key={s.l}
              className="border border-white/10 bg-[#121212] p-4"
              data-testid={`total-${s.l.toLowerCase()}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="label-eyebrow text-white/50">{s.l}</div>
                {s.t ? (
                  <div className={`text-[10px] font-bold uppercase tracking-wider ${over ? "text-[#FF3B30]" : "text-white/40"}`}>
                    {pct}%
                  </div>
                ) : null}
              </div>
              <div className="font-display text-3xl">
                {s.v}
                {s.t ? (
                  <span className="text-base text-white/40 ml-1">/ {s.t}{s.u}</span>
                ) : (
                  <span className="text-base text-white/40 ml-1">{s.u}</span>
                )}
              </div>
              {s.t ? (
                <div className="mt-3 h-1.5 bg-white/10" data-testid={`progress-${s.l.toLowerCase()}`}>
                  <div
                    className="h-full transition-all"
                    style={{ width: `${pct}%`, background: over ? "#FF3B30" : s.color }}
                  />
                </div>
              ) : (
                <div className="mt-3 h-1.5" />
              )}
            </div>
          );
        })}
      </div>

      {!targets && (
        <div className="border border-[#FFB020]/30 bg-[#FFB020]/5 px-4 py-3 mb-6 text-xs text-white/70 flex items-center justify-between" data-testid="no-targets-banner">
          <span>
            Set a <strong className="text-white">weight goal</strong> to unlock daily macro targets and progress bars.
          </span>
          <a href="/app/goals" className="text-[#6B9BFF] uppercase tracking-widest font-bold hover:underline">
            Set goal →
          </a>
        </div>
      )}

      {/* Copy-meals action bar — context-aware: copy yesterday to today
          when viewing an empty today, or copy a past day forward when
          browsing history. */}
      {(isToday && today.items.length === 0) || !isToday ? (
        <div
          className="mb-6 border border-[#6B9BFF]/25 bg-[#6B9BFF]/[0.06] px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
          data-testid="copy-meals-bar"
        >
          <div className="text-xs sm:text-sm text-white/80 flex items-center gap-2 min-w-0">
            <Copy className="w-3.5 h-3.5 text-[#6B9BFF] shrink-0" />
            {isToday
              ? "Today's empty. Want to start from yesterday's plate?"
              : `Hit repeat — copy ${friendlyLabel(selectedDate).toLowerCase()}'s meals into today's log.`}
          </div>
          <button
            type="button"
            onClick={isToday ? copyFromPrevDay : copyThisDayToToday}
            disabled={copying}
            className="inline-flex items-center gap-2 bg-[#6B9BFF] text-black font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-white disabled:opacity-50 transition-colors"
            data-testid="copy-meals-cta"
          >
            {copying ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Copy className="w-3.5 h-3.5" />
            )}
            {isToday ? "Copy yesterday's meals" : "Copy to today"}
          </button>
        </div>
      ) : null}

      {/* Add-meal cards are only available while viewing TODAY — past days
          are read-only so the user can flick through history without
          accidentally polluting old entries. */}
      {isToday && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <PhotoMealCard onLogged={load} />

        <div className={`tracing-border p-6 ${mode === "manual" ? "hidden lg:block" : ""}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="label-eyebrow text-[#6B9BFF] flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> AI quick log
            </div>
            <button
              onClick={() => setMode("manual")}
              className="text-xs text-white/50 hover:text-white"
              data-testid="switch-to-manual"
            >
              Switch to manual →
            </button>
          </div>
          <div className="space-y-3">
            <textarea
              rows={3}
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder='e.g. "2 scrambled eggs, slice of whole-wheat toast, black coffee"'
              className="w-full bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
              data-testid="ai-food-desc"
            />
            <div className="flex gap-3">
              <select
                value={aiMeal}
                onChange={(e) => setAiMeal(e.target.value)}
                className="bg-[#1A1A1A] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#6B9BFF]"
                data-testid="ai-meal-select"
              >
                {MEALS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
              <button
                disabled={estimating}
                onClick={aiAdd}
                className="flex-1 bg-gradient-to-r from-[#7C3AED] to-[#6B9BFF] text-white font-black uppercase tracking-widest px-6 py-3 inline-flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="ai-log-button"
              >
                {estimating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Estimate & log
              </button>
            </div>
          </div>
        </div>

        <div className={`border border-white/10 bg-[#121212] p-6 ${mode === "ai" ? "hidden lg:block" : ""}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="label-eyebrow text-white/50">Manual entry</div>
            <button
              onClick={() => setMode("ai")}
              className="text-xs text-white/50 hover:text-white"
              data-testid="switch-to-ai"
            >
              ← AI log
            </button>
          </div>
          <form onSubmit={manualAdd} className="grid grid-cols-2 gap-3" data-testid="manual-form">
            <input
              required
              placeholder="Food name"
              value={m.name}
              onChange={(e) => setM({ ...m, name: e.target.value })}
              className="col-span-2 bg-[#1A1A1A] border border-white/10 px-3 py-2.5 focus:outline-none focus:border-[#6B9BFF] text-sm"
              data-testid="manual-name"
            />
            <input
              required
              type="number"
              placeholder="Calories"
              value={m.calories}
              onChange={(e) => setM({ ...m, calories: e.target.value })}
              className="bg-[#1A1A1A] border border-white/10 px-3 py-2.5 focus:outline-none focus:border-[#6B9BFF] text-sm"
              data-testid="manual-calories"
            />
            <select
              value={m.meal}
              onChange={(e) => setM({ ...m, meal: e.target.value })}
              className="bg-[#1A1A1A] border border-white/10 px-3 py-2.5 focus:outline-none focus:border-[#6B9BFF] text-sm"
              data-testid="manual-meal"
            >
              {MEALS.map((mm) => (
                <option key={mm} value={mm}>{mm}</option>
              ))}
            </select>
            <input
              type="number"
              placeholder="Protein g"
              value={m.protein_g}
              onChange={(e) => setM({ ...m, protein_g: e.target.value })}
              className="bg-[#1A1A1A] border border-white/10 px-3 py-2.5 focus:outline-none focus:border-[#6B9BFF] text-sm"
              data-testid="manual-protein"
            />
            <input
              type="number"
              placeholder="Carbs g"
              value={m.carbs_g}
              onChange={(e) => setM({ ...m, carbs_g: e.target.value })}
              className="bg-[#1A1A1A] border border-white/10 px-3 py-2.5 focus:outline-none focus:border-[#6B9BFF] text-sm"
              data-testid="manual-carbs"
            />
            <input
              type="number"
              placeholder="Fat g"
              value={m.fat_g}
              onChange={(e) => setM({ ...m, fat_g: e.target.value })}
              className="bg-[#1A1A1A] border border-white/10 px-3 py-2.5 focus:outline-none focus:border-[#6B9BFF] text-sm"
              data-testid="manual-fat"
            />
            <button
              disabled={saving}
              type="submit"
              className="col-span-2 bg-[#6B9BFF] text-black font-black uppercase tracking-widest py-3 hover:bg-white transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="manual-submit"
            >
              <Plus className="w-4 h-4" /> Add meal
            </button>
          </form>
        </div>
      </div>
      )}

      <div className="space-y-6">
        {byMeal.map((g) => (
          <div key={g.meal} className="border border-white/10 bg-[#121212]" data-testid={`meal-section-${g.meal}`}>
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <div className="font-display text-xl">{g.meal}</div>
              <div className="text-xs text-white/40">
                {g.items.reduce((a, i) => a + (i.calories || 0), 0)} kcal
              </div>
            </div>
            {g.items.length === 0 ? (
              <div className="p-5 text-sm text-white/40 italic">No items logged</div>
            ) : (
              <ul className="divide-y divide-white/5">
                {g.items.map((it) => (
                  <li key={it.id} className="p-5 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-white truncate">{it.name}</span>
                        {it.ai_generated && (
                          <span className="text-[10px] uppercase tracking-wider bg-[#7C3AED]/20 text-[#7C3AED] px-1.5 py-0.5">AI</span>
                        )}
                      </div>
                      <div className="text-xs text-white/50 mt-1">
                        {Math.round(it.protein_g)}P · {Math.round(it.carbs_g)}C · {Math.round(it.fat_g)}F
                      </div>
                    </div>
                    <div className="font-display text-xl text-[#6B9BFF]">{it.calories}</div>
                    {isToday && (
                      <button
                        onClick={() => del(it.id)}
                        className="text-white/30 hover:text-[#FF3B30] transition-colors"
                        data-testid={`delete-${it.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* AI Meal Plan — sits below the day's logged meals */}
      <div className="mt-8">
        <MealPlanSection onFoodLogged={load} />
      </div>

      <MacroSplitModal
        open={macroModalOpen}
        onOpenChange={(o) => {
          setMacroModalOpen(o);
          if (!o) load(); // refresh targets after save
        }}
        kcalTarget={targets?.calories || 2200}
      />
    </div>
  );
}
